import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocketOptional } from '@/contexts/SocketContext';
import { notificationService, type MessageChannel } from '@/services/notification.service';
import { useAuth } from '@/contexts/AuthContext';
import { toast as showToast } from '@/hooks/use-toast';
import { ToastAction } from '@easyflow/ui-frontend/components/ui/toast';
import {
  useNotificationGate,
  type NotificationCategory,
  type NotificationGate,
} from '@/hooks/useNotificationPreferences';
import { channelForCategory } from '@easyflow/shared';

interface CallEvent {
  uuid: string;
  caller_name?: string;
  caller_number?: string;
  caller_id_name?: string;
  caller_id_number?: string;
  destination?: string;
  direction?: string;
  state?: string;
}

interface MessageEvent {
  conversation_id?: string;
  message?: {
    id?: string;
    from?: string;
    body?: string;
    channel?: MessageChannel;
    direction?: 'inbound' | 'outbound';
  };
  id?: string;
  from?: string;
  body?: string;
  channel?: MessageChannel;
  direction?: 'inbound' | 'outbound';
  customer_name?: string;
}

interface LeadEvent {
  lead_id?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  source?: string;
  form_name?: string;
}

interface QueueAgentStateEvent {
  extension: string;
  state: string;
  agentName?: string;
  queueName?: string;
}

interface CallbackRequestEvent {
  phone: string;
  name?: string;
  agent_name?: string;
  destination?: string;
  timestamp?: string;
}

/**
 * Typed shape of the `metadata` bag carried by a `notification:new` event.
 * Only the fields actually consumed by the desktop dispatch are declared —
 * keeping it explicit (no `any`) while remaining tolerant of optional fields.
 */
interface NotificationMetadata {
  channel?: MessageChannel;
  message_id?: string;
  email_account_id?: string;
  caller_name?: string;
  caller_number?: string;
  contact_type?: string;
  duration?: number;
  sender_name?: string;
  sender_number?: string;
  invoice_id?: string;
  event_type?: string;
  queue_name?: string;
  id?: string;
}

interface NotificationNewEvent {
  type: string;
  title: string;
  body?: string;
  actionUrl?: string;
  metadata?: NotificationMetadata;
}

/**
 * Resolves a server event/notification type to the unified preferences
 * category. Mirrors the backend TYPE_TO_CATEGORY map in
 * `packages/backend/src/services/notifications/notification.service.ts`. Unknown types
 * fall back to `system_alert` so the gate is *always* consulted — no
 * notification path silently bypasses user preferences.
 */
const TYPE_TO_CATEGORY: Record<string, NotificationCategory> = {
  missed_call: 'missed_calls',
  voicemail: 'voicemail',
  queue_timeout: 'missed_calls',
  call_transfer: 'incoming_calls',
  system_alert: 'system_alert',
  service_alert: 'service_alert',
  storage_alert: 'storage_alert',
  warning: 'warning',
  email_received: 'email_received',
  crm_event: 'crm_event',
  task_event: 'task_event',
  video_invitation: 'video_invitation',
  support_ticket: 'support_ticket',
  workgroup_invite: 'workgroup_invite',
  // Task #3822 — invito ai Progetti del PA (stessa categoria dei gruppi di lavoro).
  pa_project_invite: 'workgroup_invite',
  hr_request: 'hr_request',
  appointment_reassigned: 'appointment_reassigned',
  invoice_lifecycle: 'invoice_lifecycle',
  invoice_rejected: 'invoice_lifecycle',
  feed_post: 'feed_post',
  feed_comment: 'feed_comment',
  feed_mention: 'feed_mention',
  feed_reaction: 'feed_reaction',
  workflow_notification: 'workflow_notification',
  lead_inbound: 'lead_inbound',
  sip_admin: 'sip_admin',
  fiscal_z_mismatch: 'pos_fiscal',
  fiscal_receipt_expired: 'pos_fiscal',
  pos_receipt_discrepancy: 'pos_logistics',
  pos_inventory_variance: 'pos_logistics',
  pos_supplier_import_failed: 'pos_logistics',
  pos_transfer_stale: 'pos_logistics',
  chat_message: 'chat_message',
  chat_mention: 'chat_mention',
  incentivazione_health: 'service_alert',
  incentivazione_recalc: 'service_alert',
};

function categoryForNotificationType(type: string, metadata: NotificationMetadata): NotificationCategory {
  // SMS / WhatsApp inbound share the `message_received` server type and are
  // disambiguated through the channel metadata.
  if (type === 'message_received') {
    return metadata?.channel === 'whatsapp'
      ? 'whatsapp_inbound'
      : metadata?.channel === 'telegram'
        ? 'telegram_inbound'
        : 'sms_inbound';
  }
  // I reminder calendario condividono il type `calendar_reminder`: la categoria
  // reale (appuntamento / task / videocall) vive in `metadata.event_type`, ed è
  // ciò su cui il backend ha già fatto il gating per-veicolo. Allinearci qui
  // evita che il popup desktop usi la categoria sbagliata nel re-gate.
  if (type === 'calendar_reminder') {
    return metadata?.event_type === 'task'
      ? 'calendar_task'
      : metadata?.event_type === 'videocall'
        ? 'calendar_videocall'
        : 'calendar_appointment';
  }
  return TYPE_TO_CATEGORY[type] ?? 'system_alert';
}

/**
 * Decides whether a desktop (OS-level) popup should fire for a given category.
 * Desktop popups are the visible push channel, so we gate strictly on the
 * `push` vehicle (and the OS-level permission). The `in_app` vehicle
 * controls bell-row delivery and is enforced separately by the bell UI —
 * having `push` OFF and `in_app` ON must NOT produce a desktop popup.
 */
function shouldFireDesktop(gate: NotificationGate, category: NotificationCategory): boolean {
  if (!notificationService.enabled) return false;
  return gate.isAllowed(category, 'push');
}

export function NotificationHandler() {
  const socketContext = useSocketOptional();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Mounting `useNotificationGate` here ensures preferences are fetched at
  // app boot (NotificationHandler is mounted globally in App.tsx) and shared
  // with any other consumer through React Query's cache.
  // Il componente è montato anche sulle rotte pubbliche: le preferenze sono
  // private, quindi la query parte soltanto dopo che AuthContext ha risolto un
  // utente tenant. Evita 401 attesi e rumore in console sulla pagina di login.
  const gate = useNotificationGate(Boolean(user?.tenant_id));
  const gateRef = useRef(gate);
  useEffect(() => { gateRef.current = gate; }, [gate]);

  // Log mount - rooms are now auto-joined by backend, no manual subscribe needed
  useEffect(() => {
    console.log('[NotificationHandler] Component mounted, socket:', !!socketContext?.socket, 'connected:', socketContext?.isConnected, 'tenant:', user?.tenant_id);
  }, [socketContext?.isConnected, socketContext?.socket, user?.tenant_id]);

  const handleIncomingCall = useCallback((data: CallEvent) => {
    console.log('[NotificationHandler] call:created received:', data);

    // Accept any incoming/early call state
    if (data.direction !== 'inbound' && data.direction !== 'incoming') return;

    if (!shouldFireDesktop(gateRef.current, 'incoming_calls')) {
      console.log('[NotificationHandler] Suppressed by user preferences (incoming_calls).');
      return;
    }

    // Use caller_name/caller_number from backend, fallback to caller_id_* for compatibility
    const callerNumber = data.caller_number || data.caller_id_number || '';
    const callerName = data.caller_name || data.caller_id_name || callerNumber || 'Sconosciuto';

    console.log('[NotificationHandler] Showing call notification for:', callerName, callerNumber);
    notificationService.showCall(callerName, callerNumber, () => {
      navigate('/switchboard');
    });
  }, [navigate]);

  const handleNewMessage = useCallback((data: MessageEvent) => {
    console.log('[NotificationHandler] messaging:new_message received:', data);
    const message = data.message || data;
    const direction = message.direction || data.direction;
    if (direction !== 'inbound') {
      console.log('[NotificationHandler] Skipping non-inbound message notification.', { direction });
      return;
    }
    const senderName = data.customer_name || message.from || 'Sconosciuto';
    const body = message.body || '';
    const channel: MessageChannel = message.channel || data.channel || 'sms';
    const messageId = message.id;

    const cat: NotificationCategory =
      channel === 'whatsapp' ? 'whatsapp_inbound'
      : channel === 'telegram' ? 'telegram_inbound'
      : 'sms_inbound';
    if (!shouldFireDesktop(gateRef.current, cat)) {
      console.log(`[NotificationHandler] Suppressed by user preferences (${cat}).`);
      return;
    }

    console.log('[NotificationHandler] Showing message notification:', { senderName, channel, body });
    notificationService.showMessage(senderName, channel, body, messageId, () => {
      if (data.conversation_id) {
        navigate(`/messaging/chat?conversation=${data.conversation_id}`);
      } else {
        navigate('/messaging/chat');
      }
    });
  }, [navigate]);

  const handleNewLead = useCallback((data: LeadEvent) => {
    console.log('[NotificationHandler] lead event received:', data);

    if (!shouldFireDesktop(gateRef.current, 'crm_event')) {
      console.log('[NotificationHandler] Suppressed by user preferences (crm_event).');
      return;
    }

    const name = data.name ||
      (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : '') ||
      'Nuovo Lead';
    const source = data.form_name || data.source || 'Web Form';

    console.log('[NotificationHandler] Showing lead notification:', { name, source });
    notificationService.showLead(name, source, () => {
      navigate('/crm?tab=leads');
    });
  }, [navigate]);

  const handleCallbackRequest = useCallback((data: CallbackRequestEvent) => {
    console.log('[NotificationHandler] chatbot:callback_request received:', data);
    if (!shouldFireDesktop(gateRef.current, 'crm_event')) {
      console.log('[NotificationHandler] Suppressed by user preferences (callback / crm_event).');
      return;
    }
    notificationService.showCallbackRequest(
      data.phone,
      data.name || '',
      data.agent_name,
      () => navigate('/switchboard')
    );
  }, [navigate]);

  // Task #678 — Persistent toast when an A-Cube fiscal printer goes offline.
  // Keep one toast id per printer so we can dismiss & replace on recovery.
  const printerToastsRef = useRef<Map<string, ReturnType<typeof showToast>>>(new Map());

  const handlePrinterOffline = useCallback((data: {
    id: string; name: string; brand?: string; provider_kind?: string; status?: 'warning' | 'offline';
  }) => {
    if (!data?.id) return;
    const existing = printerToastsRef.current.get(data.id);
    if (existing) existing.dismiss();
    const slug = window.location.pathname.split('/')[1] || '';
    const path = `/${slug}/cassa?tab=impostazioni-cassa&subtab=fiscal-printers&highlight=${encodeURIComponent(data.id)}`;
    const t = showToast({
      title: data.status === 'warning' ? 'Registratore in difficoltà' : 'Registratore non raggiungibile',
      description: `${data.name}${data.brand ? ` (${data.brand})` : ''} non risponde. Le ricevute potrebbero non essere emesse.`,
      variant: 'destructive',
      duration: 1000 * 60 * 60, // ~1h, of fact persistent until dismissed/recovered
      action: (
        <ToastAction altText="Apri Stato RT" onClick={() => navigate(path)}>
          Apri Stato RT
        </ToastAction>
      ),
    });
    printerToastsRef.current.set(data.id, t);
  }, [navigate]);

  const handlePrinterOnline = useCallback((data: { id: string; name: string }) => {
    if (!data?.id) return;
    const existing = printerToastsRef.current.get(data.id);
    if (existing) {
      existing.dismiss();
      printerToastsRef.current.delete(data.id);
    }
    showToast({
      title: 'Registratore di nuovo online',
      description: `${data.name} è di nuovo raggiungibile.`,
      duration: 5000,
    });
  }, []);

  const handleVideoInvite = useCallback((data: { roomId: string; roomTitle: string; inviterName: string }) => {
    console.log('[NotificationHandler] video:invite received:', data);
    if (!shouldFireDesktop(gateRef.current, 'video_invitation')) {
      console.log('[NotificationHandler] Suppressed by user preferences (video_invitation).');
      return;
    }
    const slug = window.location.pathname.split('/')[1] || '';
    notificationService.showChannel(
      'video',
      'Invito Video Call',
      `${data.inviterName} ti ha invitato a: ${data.roomTitle}`,
      {
        tag: `video-invite-${data.roomId}`,
        onClick: () => navigate(`/${slug}/video/${data.roomId}`),
      },
    );
  }, [navigate]);

  const handleNotificationNew = useCallback((data: NotificationNewEvent) => {
    console.log('[NotificationHandler] notification:new received:', data);

    const metadata: NotificationMetadata = data.metadata || {};
    const cat = categoryForNotificationType(data.type, metadata);
    if (!shouldFireDesktop(gateRef.current, cat)) {
      console.log(`[NotificationHandler] Suppressed by user preferences (${cat}).`);
      return;
    }
    if (data.type === 'email_received' && metadata.email_account_id) {
      if (!gateRef.current.isEmailAccountAllowed(metadata.email_account_id)) {
        console.log('[NotificationHandler] Suppressed by per-account email override.');
        return;
      }
    }

    // A small set of types render with bespoke formatting (contact badges,
    // duration, channel). Everything else falls through to a single generic
    // dispatch so that *no* type can clear `shouldFireDesktop` and yet never
    // produce a desktop popup (Task #2395 — the old rigid if/else chain
    // silently dropped every type not explicitly listed).
    if (data.type === 'missed_call') {
      notificationService.showMissedCall(
        metadata.caller_name || data.title,
        metadata.caller_number || '',
        metadata.contact_type === 'customer' ? '[Cliente]' : metadata.contact_type === 'lead' ? '[Lead]' : undefined,
        () => navigate(data.actionUrl || '/calls?tab=missed')
      );
    } else if (data.type === 'voicemail') {
      const duration = metadata.duration 
        ? (metadata.duration >= 60 ? `${Math.floor(metadata.duration / 60)}m ${metadata.duration % 60}s` : `${metadata.duration}s`)
        : '';
      notificationService.showVoicemail(
        metadata.caller_name || metadata.caller_number || data.title,
        duration,
        metadata.contact_type === 'customer' ? '[Cliente]' : metadata.contact_type === 'lead' ? '[Lead]' : undefined,
        () => navigate(data.actionUrl || '/voicemail')
      );
    } else if (data.type === 'message_received') {
      const channel: MessageChannel = metadata.channel || 'sms';
      notificationService.showMessage(
        metadata.sender_name || metadata.sender_number || data.title,
        channel,
        data.body || '',
        metadata.message_id,
        () => navigate(data.actionUrl || '/messaging/chat')
      );
    } else {
      // Generic, type-agnostic dispatch. Special behaviours preserved:
      //  - invoice_rejected stays on screen until interacted with (Task #680);
      //  - the deep-link is the backend-provided, already-validated actionUrl
      //    (NotificationService.create normalizes it per type), with /dashboard
      //    as the only safe fallback.
      const isRejection = data.type === 'invoice_rejected';
      const tagSeed = metadata.id || metadata.invoice_id || metadata.event_type || Date.now();
      notificationService.showChannel(
        channelForCategory(cat),
        data.title,
        data.body || '',
        {
          tag: `${data.type}-${tagSeed}`,
          requireInteraction: isRejection,
          onClick: () => navigate(data.actionUrl || '/dashboard'),
        },
      );
    }
  }, [navigate]);

  // Handle queue:agent-state for queue/IVR calls - notify when agent's extension is ringing
  const handleQueueAgentState = useCallback((data: QueueAgentStateEvent) => {
    console.log('[NotificationHandler] queue:agent-state received:', data);
    
    // Only notify on 'Receiving' state (agent extension is ringing)
    if (data.state !== 'Receiving') return;
    
    // Filter for current user's extension
    const userExtension = user?.extension?.toString();
    if (!userExtension || data.extension !== userExtension) {
      console.log('[NotificationHandler] Ignoring - not my extension:', data.extension, 'vs', userExtension);
      return;
    }
    
    if (!shouldFireDesktop(gateRef.current, 'incoming_calls')) {
      console.log('[NotificationHandler] Suppressed by user preferences (queue / incoming_calls).');
      return;
    }
    console.log('[NotificationHandler] Showing queue call notification for extension:', data.extension);
    notificationService.showCall(
      `Chiamata dalla coda: ${data.queueName || 'Coda'}`,
      data.agentName || data.extension,
      () => navigate('/switchboard')
    );
  }, [navigate, user?.extension]);

  const handlersRef = useRef({
    handleIncomingCall,
    handleNewMessage,
    handleNewLead,
    handleNotificationNew,
    handleQueueAgentState,
    handleCallbackRequest,
    handleVideoInvite,
    handlePrinterOffline,
    handlePrinterOnline,
  });
  useEffect(() => {
    handlersRef.current = {
      handleIncomingCall,
      handleNewMessage,
      handleNewLead,
      handleNotificationNew,
      handleQueueAgentState,
      handleCallbackRequest,
      handleVideoInvite,
      handlePrinterOffline,
      handlePrinterOnline,
    };
  }, [handleIncomingCall, handleNewMessage, handleNewLead, handleNotificationNew, handleQueueAgentState, handleCallbackRequest, handleVideoInvite, handlePrinterOffline, handlePrinterOnline]);

  // Handle deep-link navigation requests sent by the Service Worker on
  // notification click (sw.js → postMessage({ type: 'NAVIGATE', url })).
  // We use the SPA router so we don't trigger a full page reload that could
  // drop in-progress calls or other transient state.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    type SwNavigatePayload = { type: 'NAVIGATE'; url: string };
    const isNavigatePayload = (value: unknown): value is SwNavigatePayload => {
      if (!value || typeof value !== 'object') return false;
      const v = value as Record<string, unknown>;
      return v.type === 'NAVIGATE' && typeof v.url === 'string';
    };
    // The local `MessageEvent` interface declared at the top of this file
    // shadows the DOM type, so we type the listener through the DOM lib's
    // namespace and read `.data` via a runtime guard rather than a cast.
    const onSwMessage = (event: globalThis.MessageEvent) => {
      const payload: unknown = event.data;
      if (!isNavigatePayload(payload)) return;
      try {
        const url = payload.url;
        if (/^https?:\/\//i.test(url)) {
          // Same-origin only; never let a SW push redirect us off-origin.
          if (!url.startsWith(window.location.origin)) return;
          const path = url.slice(window.location.origin.length) || '/';
          navigate(path);
        } else if (url.startsWith('/')) {
          navigate(url);
        }
      } catch (err) {
        console.warn('[NotificationHandler] SW navigate failed', err);
      }
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);

    // Tell the SW we have a navigate listener mounted — without this signal,
    // sw.js falls back to a hard `client.navigate()` (page reload) on
    // notification click. We re-announce on every controllerchange so a fresh
    // SW (e.g. after activate) learns about us too.
    const announceReady = () => {
      try {
        const ctl = navigator.serviceWorker.controller;
        if (ctl) ctl.postMessage({ type: 'NAV_LISTENER_READY' });
      } catch { /* ignore */ }
    };
    announceReady();
    navigator.serviceWorker.addEventListener('controllerchange', announceReady);

    return () => {
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
      navigator.serviceWorker.removeEventListener('controllerchange', announceReady);
      try {
        const ctl = navigator.serviceWorker.controller;
        if (ctl) ctl.postMessage({ type: 'NAV_LISTENER_TEARDOWN' });
      } catch { /* ignore */ }
    };
  }, [navigate]);

  useEffect(() => {
    if (!socketContext?.socket || !socketContext?.isConnected) return;

    const socket = socketContext.socket;

    const onAnyHandler = (eventName: string, ...args: unknown[]) => {
      if (eventName === 'queue:update') return;
      if (eventName.startsWith('call:') || eventName.startsWith('messaging:') || eventName.startsWith('crm:') || eventName.startsWith('queue:')) {
        console.log(`[NotificationHandler] 🔔 Event received: ${eventName}`, args);
      }
    };
    socket.onAny(onAnyHandler);

    const onCall = (data: CallEvent) => handlersRef.current.handleIncomingCall(data);
    const onMessage = (data: MessageEvent) => handlersRef.current.handleNewMessage(data);
    const onLead = (data: LeadEvent) => handlersRef.current.handleNewLead(data);
    const onNotification = (data: NotificationNewEvent) => handlersRef.current.handleNotificationNew(data);
    const onQueueState = (data: QueueAgentStateEvent) => handlersRef.current.handleQueueAgentState(data);
    const onCallback = (data: CallbackRequestEvent) => handlersRef.current.handleCallbackRequest(data);
    const onVideoInvite = (data: { roomId: string; roomTitle: string; inviterName: string }) => handlersRef.current.handleVideoInvite(data);

    socket.on('call:created', onCall);
    socket.on('call:ringing', onCall);
    socket.on('messaging:new_message', onMessage);
    socket.on('crm:lead_matched', onLead);
    socket.on('crm:new_lead', onLead);
    socket.on('notification:new', onNotification);
    socket.on('queue:agent-state', onQueueState);
    socket.on('chatbot:callback_request', onCallback);
    socket.on('video:invite', onVideoInvite);

    // Task #3976 — notification:state_update: aggiornamenti real-time di
    // acknowledged_at / resolved_at emessi dal backend quando il dominio chiude
    // una pratica (resolveSystemAlerts / resolveByReference) o l'utente preme
    // "Prendi in carico". NotificationHandler lo riemette come evento globale
    // così qualsiasi consumer (es. dashboard) può reagire senza dipendere dal
    // singleton NotificationBell (che gestisce anche il proprio stato locale).
    const onStateUpdate = (data: { id?: string; acknowledged_at?: string | null; resolved_at?: string | null; dedupeKey?: string }) => {
      window.dispatchEvent(new CustomEvent('notification:state_update', { detail: data }));
    };
    socket.on('notification:state_update', onStateUpdate);

    const onPrinterOffline = (data: { id: string; name: string; brand?: string; provider_kind?: string; status?: 'warning' | 'offline' }) => handlersRef.current.handlePrinterOffline(data);
    const onPrinterOnline = (data: { id: string; name: string }) => handlersRef.current.handlePrinterOnline(data);
    socket.on('pos:printer:offline', onPrinterOffline);
    socket.on('pos:printer:online', onPrinterOnline);

    return () => {
      socket.offAny(onAnyHandler);
      socket.off('call:created', onCall);
      socket.off('call:ringing', onCall);
      socket.off('messaging:new_message', onMessage);
      socket.off('crm:lead_matched', onLead);
      socket.off('crm:new_lead', onLead);
      socket.off('notification:new', onNotification);
      socket.off('queue:agent-state', onQueueState);
      socket.off('chatbot:callback_request', onCallback);
      socket.off('video:invite', onVideoInvite);
      socket.off('notification:state_update', onStateUpdate);
      socket.off('pos:printer:offline', onPrinterOffline);
      socket.off('pos:printer:online', onPrinterOnline);
    };
  }, [socketContext?.socket, socketContext?.isConnected]);

  return null;
}
